import { useEffect, useRef } from "react";
import type { SiteImage } from "../models/site";
import { useLightboxPresenter } from "../presenters/useInteractionPresenters";
import { SmartImage } from "./SmartImage";

type ImageGalleryProps = {
  images: readonly SiteImage[];
  dialogLabel: string;
  eagerCount?: number;
};

export function ImageGallery({
  images,
  dialogLabel,
  eagerCount = 0,
}: ImageGalleryProps) {
  const lightbox = useLightboxPresenter(images.length);
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (lightbox.index !== null) closeButton.current?.focus();
  }, [lightbox.index]);

  const selectedImage = lightbox.index === null ? null : images[lightbox.index];

  return (
    <>
      <div className="masonry-grid image-gallery">
        {images.map((image, index) => (
          <button
            type="button"
            className="gallery-item"
            key={image.name}
            onClick={() => lightbox.open(index)}
            aria-label={`放大圖片 View image: ${image.alt}`}
          >
            <SmartImage
              name={image.name}
              alt={image.alt}
              width={image.width}
              height={image.height}
              sizes="(max-width: 767px) 50vw, 33vw"
              eager={index < eagerCount}
            />
          </button>
        ))}
      </div>

      {selectedImage && (
        <div
          className="lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={dialogLabel}
          onMouseDown={(event) =>
            event.target === event.currentTarget && lightbox.close()
          }
        >
          <button
            ref={closeButton}
            className="lightbox-close"
            type="button"
            onClick={lightbox.close}
            aria-label="關閉圖片 Close image"
          >
            ×
          </button>
          <button
            type="button"
            className="lightbox-nav previous"
            onClick={lightbox.previous}
            aria-label="上一張 Previous image"
          >
            ←
          </button>
          <figure>
            <SmartImage
              name={selectedImage.name}
              alt={selectedImage.alt}
              width={selectedImage.width}
              height={selectedImage.height}
              sizes="90vw"
              eager
            />
            <figcaption>{selectedImage.alt}</figcaption>
          </figure>
          <button
            type="button"
            className="lightbox-nav next"
            onClick={lightbox.next}
            aria-label="下一張 Next image"
          >
            →
          </button>
        </div>
      )}
    </>
  );
}
